import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["columns", "data", "tableRef", "modalTable"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialReactTable } from 'material-react-table';
import { alpha, Box, selectClasses, Typography } from '@mui/material';
import { SelectMenuStyles, SelectPaperStyles } from '@components/table/tables.styles';
import { Icon } from '@elements';
import { SelectIcon } from '@elements/form/fields/select-field/select-field.helpers';
import { colors, defaultTheme, pxToRem } from '@themes';
import checkedIcon from '@images/checkbox-checked.svg';
import notFound from '@images/not-found.svg';
import sort from '@images/sort.svg';
import uncheckedIcon from '@images/unchecked.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

/**
 * Table component.
 *
 * @author Ihar Kazlouski
 * @function TableComponent
 * @category components
 * @return {FC} table component.
 */
function TableComponent(_ref) {
  var _sx2, _sx3;

  var columns = _ref.columns,
      data = _ref.data,
      tableRef = _ref.tableRef,
      modalTable = _ref.modalTable,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsx(MaterialReactTable, _objectSpread({
    columns: columns,
    enableColumnActions: false,
    tableInstanceRef: tableRef,
    data: data,
    positionPagination: "top",
    enableBottomToolbar: false,
    positionActionsColumn: "last",
    initialState: {
      showColumnFilters: false
    },
    positionToolbarAlertBanner: "none",
    renderEmptyRowsFallback: function renderEmptyRowsFallback(_ref2) {
      var table = _ref2.table;
      return /*#__PURE__*/_jsx(Box, {
        sx: {
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          height: pxToRem(400)
        },
        children: /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center'
          },
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              border: "".concat(pxToRem(1), " solid ").concat(colors.greenLight),
              borderRadius: pxToRem(40),
              padding: pxToRem(12)
            },
            children: /*#__PURE__*/_jsx(Box, {
              sx: {
                borderRadius: pxToRem(40),
                padding: pxToRem(30),
                backgroundColor: alpha(colors.greenLight, 0.2)
              },
              children: /*#__PURE__*/_jsx(Icon, {
                src: notFound,
                width: 44,
                height: 44
              })
            })
          }), /*#__PURE__*/_jsx(Box, {
            pt: 3,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: pxToRem(12),
            children: /*#__PURE__*/_jsx(Typography, {
              variant: "body1",
              fontWeight: "700",
              color: "mineShaft",
              textAlign: "center",
              children: t('components.table.empty')
            })
          })]
        })
      });
    },
    muiTableContainerProps: {
      sx: {
        overflow: 'unset'
      }
    },
    muiTablePaginationProps: function muiTablePaginationProps(_ref3) {
      var table = _ref3.table;
      var selectedRowLength = table.getSelectedRowModel().rows.length;
      return {
        showFirstButton: false,
        showLastButton: false,
        rowsPerPageOptions: [5, 10, 25, 50],
        SelectProps: {
          IconComponent: SelectIcon,
          MenuProps: {
            PaperProps: {
              style: SelectPaperStyles(defaultTheme)
            },
            MenuListProps: {
              sx: SelectMenuStyles(defaultTheme)
            }
          },
          sx: _defineProperty({}, "& .".concat(selectClasses.select), {
            backgroundColor: colors.whisper,
            padding: "".concat(pxToRem(12), " ").concat(pxToRem(14)),
            paddingRight: "".concat(pxToRem(44), " !important"),
            borderRadius: pxToRem(14)
          }),
          renderValue: function renderValue(selected) {
            return /*#__PURE__*/_jsxs(Box, {
              whiteSpace: "normal",
              display: "flex",
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "body2",
                color: "comet",
                children: t('components.table.rows')
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "body2",
                color: "comet",
                ml: 1.25,
                children: selected
              })]
            });
          }
        },
        sx: {
          position: 'absolute',
          zIndex: 9999,
          right: 0,
          bottom: selectedRowLength ? '29%' : '4%',
          '.MuiTablePagination-displayedRows': {
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: pxToRem(16),
            lineHeight: pxToRem(20),
            letterSpacing: '-0.003em',
            color: colors.comet
          },
          '.MuiTablePagination-selectLabel': {
            display: 'none'
          },
          '.MuiTablePagination-spacer': {
            display: 'none'
          },
          '.MuiTablePagination-actions': {
            display: 'flex',
            gap: pxToRem(8)
          },
          '.MuiIconButton-root': {
            backgroundColor: colors.funGreen,
            borderRadius: pxToRem(13),
            color: colors.white,
            '&.Mui-disabled': {
              backgroundColor: colors.whisper,
              color: colors.frenchGray
            }
          },
          '.MuiToolbar-root': {
            padding: "0 ".concat(pxToRem(48), " 0 0"),
            justifyContent: 'end'
          }
        }
      };
    },
    muiTopToolbarProps: function muiTopToolbarProps(_ref4) {
      var table = _ref4.table;
      var selectedRowLength = table.getSelectedRowModel().rows.length;
      return {
        sx: {
          minHeight: pxToRem(selectedRowLength > 0 ? 320 : 224),
          height: pxToRem(selectedRowLength > 0 ? 320 : 224),
          justifyContent: 'unset',
          flexWrap: 'wrap',
          width: '100%',
          '.MuiBox-root': {
            justifyContent: 'unset',
            flexWrap: 'wrap',
            width: '100%'
          }
        }
      };
    },
    muiTablePaperProps: {
      sx: {
        borderRadius: modalTable ? 'unset' : pxToRem(16),
        boxShadow: 'unset',
        display: 'grid',
        overflow: 'auto',
        border: "".concat(pxToRem(1), " solid ").concat(colors.athensGray)
      }
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottomColor: colors.athensGray,
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: pxToRem(16),
        lineHeight: pxToRem(20),
        letterSpacing: '-0.003em',
        color: colors.comet,
        padding: "".concat(pxToRem(34), " ").concat(pxToRem(16))
      }
    },
    muiTableHeadProps: {
      sx: {
        '.MuiTableRow-head': {
          th: {
            '&:first-child': {
              paddingLeft: pxToRem(36)
            }
          }
        }
      }
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottomColor: (data === null || data === void 0 ? void 0 : data.length) === 0 ? 'transparent' : colors.athensGray,
        color: colors.mineShaft,
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: pxToRem(14),
        lineHeight: pxToRem(18),
        paddingTop: pxToRem(24),
        paddingBottom: pxToRem(24),
        '.MuiBadge-root': {
          marginLeft: pxToRem(10)
        }
      }
    },
    muiSelectAllCheckboxProps: {
      icon: /*#__PURE__*/_jsx(Icon, {
        src: uncheckedIcon,
        width: 24,
        height: 24
      }),
      checkedIcon: /*#__PURE__*/_jsx(Icon, {
        src: checkedIcon,
        width: 24,
        height: 24
      }),
      sx: (_sx2 = {
        color: colors.frenchGray,
        padding: pxToRem(8),
        margin: "".concat(pxToRem(-11), " ").concat(pxToRem(-2), " 0")
      }, _defineProperty(_sx2, '&.Mui-checked .MuiSvgIcon-root', {
        color: colors.funGreen
      }), _defineProperty(_sx2, '&.MuiCheckbox-indeterminate .MuiSvgIcon-root', {
        color: colors.funGreen
      }), _defineProperty(_sx2, '& .MuiSvgIcon-root', {
        height: pxToRem(24)
      }), _sx2)
    },
    muiLinearProgressProps: {
      sx: {
        backgroundColor: colors.gin,
        '.MuiLinearProgress-bar': {
          backgroundColor: colors.funGreen
        }
      }
    },
    localization: {
      toggleSelectAll: '',
      toggleSelectRow: '',
      sortedByColumnAsc: '',
      sortedByColumnDesc: '',
      unsorted: ''
    },
    muiSelectCheckboxProps: {
      icon: /*#__PURE__*/_jsx(Icon, {
        src: uncheckedIcon,
        width: 24,
        height: 24
      }),
      checkedIcon: /*#__PURE__*/_jsx(Icon, {
        src: checkedIcon,
        width: 24,
        height: 24
      }),
      sx: (_sx3 = {
        color: colors.frenchGray,
        padding: pxToRem(8)
      }, _defineProperty(_sx3, '&.Mui-checked .MuiSvgIcon-root', {
        color: colors.funGreen
      }), _defineProperty(_sx3, '& .MuiSvgIcon-root', {
        height: pxToRem(24)
      }), _sx3)
    },
    muiTableBodyRowProps: {
      sx: {
        verticalAlign: 'top',
        '&.Mui-selected': {
          backgroundColor: colors.gin,
          '& .MuiTableCell-body': {
            backgroundColor: colors.gin
          },
          '&:hover': {
            backgroundColor: colors.gin
          }
        },
        '.MuiTableCell-body': {
          '&:first-child': {
            paddingLeft: pxToRem(40)
          }
        },
        '&:last-child td': {
          borderBottom: 0
        },
        '&:hover': {
          '& .MuiTableCell-body': {
            backgroundColor: colors.gin
          }
        }
      }
    },
    icons: {
      ArrowDownwardIcon: function ArrowDownwardIcon(props) {
        return /*#__PURE__*/_jsx(Icon, _objectSpread({
          src: sort,
          width: 13,
          height: 13
        }, props));
      }
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Header: ''
      }
    }
  }, restProps));
}

export { TableComponent };